import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateTime } from 'luxon';
import { firstValueFrom, throwError } from 'rxjs';
import { catchError, first } from 'rxjs/operators';
import {
  AgeVerificationUrl,
  AgeVerificationValidation,
  CheckAuthorizedResponse,
  CreateAndPrepareOrder,
  CustomerDetails,
  CustomerUserPaymentType,
  OrderContext,
  ProductComposition,
  ProductLine,
  ProductTypes,
  sgTokenSessionKey,
  WoltTrackingResponse,
} from 'src/app/models';
import { GiftCard, GiftCardType } from 'src/app/models/gift-card';
import { G_TAG_ORDER } from 'src/app/models/gtm';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from 'src/app/pages/basket/checkout/complete-step/confirmation-dialog/confirmation-dialog.compontent';
import { environment } from 'src/environments/environment';
import { AnalyticService } from '../analytics/analytics.service';
import { GtmService } from '../analytics/gtm.service';
import { AuthService } from '../auth';
import { BasketQuery } from '../basket/basket.query';
import { ContentService } from '../content/content.service';
import { DialogService } from '../dialog/dialog.service';
import { KioskService } from '../kiosk/kiosk.service';
import { ShippingQuery, ShippingService, WoltService } from '../shipping';
import { CustomerDetailsQuery } from './customer-details.query';
import { RedirectService } from './redirect.service';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private http = inject(HttpClient);
  private redirectService = inject(RedirectService);
  private contentService = inject(ContentService);
  private customerDetailsQuery = inject(CustomerDetailsQuery);
  private basketQuery = inject(BasketQuery);
  private shippingService = inject(ShippingService);
  private shippingQuery = inject(ShippingQuery);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  private gtmService = inject(GtmService);
  private woltService = inject(WoltService);
  private kioskService = inject(KioskService);
  private dialogService = inject(DialogService);
  private analyticService = inject(AnalyticService);

  private readonly checkoutUrl = `${environment.apiOrigin}/Order/CreateAndPrepareOrderForPayment`;
  private readonly checkAuthorized = `${environment.apiOrigin}/Order/CheckIfAuthorized`;
  private readonly authorizeUrl = `${environment.apiOrigin}/Payment/AuthorizeOrder`;
  private readonly woltTrackingUrl = `${environment.apiOrigin}/Wolt/TrackingLink`;
  private readonly ageVerificationEndpoint = `${environment.apiOrigin}/AgeVerification`;
  private readonly accountOrderReturnCode = 6;
  private readonly successPaymentSMSCode = 18;

  private isCheckingOutValue = false;
  public submitting: boolean;

  public paymentType: CustomerUserPaymentType;

  errorMessage: {
    text?: string;
    closeButton?: string;
  };

  load() {
    this.submitting = false;

    const content = this.contentService.get('basketShipping');
    this.errorMessage = {
      text: content.get('errorText') || 'Der skete en fejl',
      closeButton: content.get('errorCloseButtonText') || 'Luk',
    };

    return new Promise<void>((resolve) => resolve());
  }

  openConfirmationDialog(data: ConfirmationDialogData): MatDialogRef<ConfirmationDialogComponent, boolean> {
    return this.dialogService.openDialog(ConfirmationDialogComponent, data.title, { data, classes: ['confirmation-dialog'] });
  }

  async checkout() {
    if (this.submitting) {
      return;
    }

    this.submitting = true;

    // TODO: check if basket has error code, display error message
    // if (error)
    //    return;

    // Kiosk
    if (this.kioskService.useKioskShipping) {
      await this.shippingService.loadKioskShipping(false);
    }

    // Client time
    const clientTime = DateTime.now().toFormat('HH:mm:ss');

    // Basket
    const basketState = this.basketQuery.getValue().ui;
    const orderlines = basketState.products.map((orderline) => {
      const createOrderLine = { ...orderline, productName: '' };
      return createOrderLine;
    });
    const orderTotal = basketState.total;
    const packagingTotal = basketState.packaging;

    // Shipping
    const shipping = this.shippingQuery.shipping;
    const formattedShippingDate = shipping.date.toFormat('yyyy-MM-dd');

    // Event
    const eventStore = this.shippingQuery.eventStore;

    // Payment and final shipping info
    const customerDetails = this.customerDetailsQuery.getCustomerDetails();
    const payment = customerDetails.payment;
    const alternativeShipping = customerDetails.shipping;
    const newsletterConsent = customerDetails.newsletterConsent;
    const reclaim = customerDetails.reclaim;
    const customerPackaging = this.shippingQuery.allowCustomerPackaging && !!customerDetails.customerPackaging;

    if (!customerDetails.isBusiness) {
      payment.CVR = null;
      payment.EAN = null;
      payment.companyName = null;
      payment.requisition = null;
      payment.invoiceRecipientName = null;
      payment.invoiceRecipientEmail = null;
    }

    // Customer info
    const memberOrderDetails = this.authService.getPaymentDetails(this.authService.member.value);
    const ageVerified = !!sessionStorage.getItem(sgTokenSessionKey);

    // Analytics
    const clientGoogleId = await this.analyticService.getClientId();

    const order: CreateAndPrepareOrder = {
      merchantId: this.contentService.getMerchantId(),
      newsletterConsent,
      clientTime,
      clientGoogleId,
      order: {
        ...memberOrderDetails,
        customerUserPaymentType: this.paymentType,
        comment: payment.note,
        orderTotal,
        discountTotal: 0,
        packagingTotal,
        cashPayment: this.paymentType === CustomerUserPaymentType.Cash,
        customerPackaging,
        ageVerified,
        CVR: payment.CVR,
        EAN: payment.EAN,
        requisition: payment.requisition,
        invoiceRecipientName: payment.invoiceRecipientName,
        invoiceRecipientEmail: payment.invoiceRecipientEmail,
        discountCode: basketState.discountCode,
        orderlines,
        billingAddress: {
          firstName: payment.firstName,
          lastName: payment.lastName,
          email: payment.email,
          phoneNumber: payment.telephone.replace(/\s/g, ''),
          addressLine1: payment.address,
          addressLine2: undefined,
          city: payment.city,
          zip: payment.zip,
          companyName: payment.companyName,
          country: environment.i18n.country,
        },
        orderShipping: {
          amount: shipping.price,
          deliveryDate: formattedShippingDate,
          deliveryTime: shipping.time.period,
          shippingAddress: {
            firstName: alternativeShipping.firstName,
            lastName: alternativeShipping.lastName,
            phoneNumber: alternativeShipping.telephone.replace(/\s/g, ''),
            addressLine1: alternativeShipping.address,
            addressLine2: undefined,
            city: alternativeShipping.city,
            zip: alternativeShipping.zip,
            companyName: alternativeShipping.companyName,
            country: environment.i18n.country,
          },
          shippingId: shipping.shippingType,
          isWolt: shipping.isWolt,
        },
      },
      kioskOrder: this.kioskService.isKiosk,
    };

    // Wolt
    if (order.order.orderShipping.isWolt) {
      const { id, agreedPrice, lat, lon, agreedTime, agreedPickupTime, validUntil } = this.woltService.woltDelivery.value;

      order.order.orderShipping = {
        ...order.order.orderShipping,
        deliveryDate: DateTime.fromISO(agreedTime).toFormat('yyyy-MM-dd'),
        deliveryTime: DateTime.fromISO(agreedTime).toFormat('HH:mm'),
        woltId: id,
        woltAgreedPrice: agreedPrice,
        woltLat: lat,
        woltLon: lon,
        woltPickupTime: agreedPickupTime,
        woltAgreedTime: agreedTime,
      };

      if (!shipping.woltUsePriceGroups) {
        order.order.orderShipping.amount = agreedPrice / 100;
      }

      const now = DateTime.now();
      const expiration = DateTime.fromISO(validUntil);
      if (now > expiration) {
        firstValueFrom(
          this.handleError({ error: { errorMessage: 'Wolt levering er udløbet. Gå tilbage og vælg et nyt leveringstidspunkt' } }),
        );
        return;
      }
    }

    if (eventStore) {
      order.order.eventStore = eventStore;
    }

    if (basketState.giftCardCode) {
      order.order.giftCardToken = basketState.giftCardCode;
    }

    if (customerDetails.alternativeReclaimAddress && reclaim) {
      order.order.reclaimAddress = {
        firstName: undefined,
        lastName: undefined,
        phoneNumber: reclaim.telephone.replace(/\s/g, ''),
        email: reclaim.email,
        addressLine1: reclaim.address,
        addressLine2: undefined,
        city: reclaim.city,
        zip: reclaim.zip,
        companyName: undefined,
        country: environment.i18n.country,
      };
    }

    this.http
      .post<{ content: OrderContext; errorMessage: string; responseCode: number; responseCodeName: string }>(this.checkoutUrl, order)
      .pipe(
        first(),
        catchError((error) => this.handleError(error)),
      )
      .subscribe((data) => {
        // TODO: shipping error => spark shipping i røven
        // TODO: product error => spark basket i røven
        const { content } = data;
        if (data.responseCode === this.successPaymentSMSCode) {
          this.redirectService.kioskConfirm(content.orderId);
          return;
        }

        if (order.order.orderShipping.isWolt) content.isWolt = `${order.order.orderShipping.isWolt}`;
        localStorage.setItem(G_TAG_ORDER, JSON.stringify(content));

        if (data.responseCode === this.accountOrderReturnCode) {
          this.redirectService.confirm();
          return;
        }

        this.redirectService.post(content.redirectUrl);
      });
  }

  async giftCardCheckout(giftCard: GiftCard) {
    if (this.submitting) {
      return;
    }

    this.submitting = true;

    // Client time
    const clientTime = DateTime.now().toFormat('HH:mm:ss');

    // Basket
    let name = `Gavekort til ${giftCard.recipientName}`;
    if (giftCard.type === GiftCardType.Punch) name = `Klippekort med ${giftCard.charges} klip til ${giftCard.recipientName}`;

    const product: ProductComposition = {
      id: 0, // calculateBasket ignores products with id 0 (default).
      lineId: 1,
      productDisplayName: name,
      productUrl: environment.urls.gift_card,
      price: +giftCard.value,
      originalPrice: +giftCard.value,
      forSale: true,
      notForSaleLabel: undefined,
      notForSaleDescription: undefined,
      imageUrls: undefined,
      primaryCategoryId: undefined,
      categories: undefined,
      minCount: 0,
      allergens: undefined,
      shippings: undefined,
      productDeliveryLimit: undefined,
      alcoholPercent: 0,
      productCategoryTags: undefined,
      productDisplayTitle: undefined,
      productPageTitle: undefined,
      productPageDescription: undefined,
      productDescription: undefined,
      productModules: undefined,
      productAdditions: undefined,
      pdfUrls: undefined,
      displayPdfUrls: false,
      moduleChoices: undefined,
      discountLevels: undefined,
      productType: ProductTypes.Catering,
      onlineProductLinksName: undefined,
      onlineProductLinks: undefined,
      onlineImageStyle: undefined,
      hidden: false,
      backgroundImageUrl: undefined,
      backgroundImageDarken: false,
    };
    const line: ProductLine = {
      lineId: product.lineId,
      productId: product.id,
      price: product.price,
      discount: 0,
      moduleLines: [] as ProductLine[],
      quantity: 1,
      product,
      giftCardInfo: giftCard,
    };

    // Shipping
    const shipping = this.shippingService.getGiftCardShipping();
    const deliveryDate = DateTime.now().toFormat('yyyy-MM-dd');

    const orderlines = [{ ...line, productName: name }];
    const orderTotal = +giftCard.value + shipping.price;

    // Payment and final shipping info
    const customerDetailsForm = this.customerDetailsQuery.getCustomerDetails();
    const payment = customerDetailsForm.payment;

    if (!customerDetailsForm.isBusiness) {
      payment.CVR = null;
      payment.EAN = null;
      payment.companyName = null;
      payment.requisition = null;
      payment.invoiceRecipientName = null;
      payment.invoiceRecipientEmail = null;
    }

    // Customer info
    const memberOrderDetails = this.authService.getPaymentDetails(this.authService.member.value);
    const ageVerified = !!sessionStorage.getItem(sgTokenSessionKey);

    // Analytics
    const clientGoogleId = await this.analyticService.getClientId();

    const order: CreateAndPrepareOrder = {
      merchantId: this.contentService.getMerchantId(),
      newsletterConsent: false,
      clientTime,
      clientGoogleId,
      order: {
        ...memberOrderDetails,
        customerUserPaymentType: CustomerUserPaymentType.Default,
        comment: payment.note,
        orderTotal,
        discountTotal: 0,
        packagingTotal: 0,
        cashPayment: false,
        customerPackaging: true,
        ageVerified,
        CVR: payment.CVR,
        EAN: payment.EAN,
        requisition: payment.requisition,
        invoiceRecipientName: payment.invoiceRecipientName,
        invoiceRecipientEmail: payment.invoiceRecipientEmail,
        discountCode: '',
        orderlines,
        billingAddress: {
          firstName: payment.firstName,
          lastName: payment.lastName,
          email: payment.email,
          phoneNumber: payment.telephone.replace(/\s/g, ''),
          addressLine1: payment.address,
          addressLine2: undefined,
          city: payment.city,
          zip: payment.zip,
          companyName: payment.companyName,
          country: environment.i18n.country,
        },
        orderShipping: {
          amount: shipping.price,
          deliveryDate,
          deliveryTime: clientTime,
          shippingAddress: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            addressLine1: '',
            addressLine2: undefined,
            city: '',
            zip: '',
            companyName: '',
            country: environment.i18n.country,
          },
          shippingId: shipping.id,
          isWolt: false,
        },
      },
      kioskOrder: false,
    };

    this.http
      .post<{ content: OrderContext; errorMessage: string; responseCode: number; responseCodeName: string }>(this.checkoutUrl, order)
      .pipe(
        first(),
        catchError((error) => this.handleError(error)),
      )
      .subscribe((data) => {
        const { content } = data;

        localStorage.setItem(G_TAG_ORDER, JSON.stringify(content));

        this.redirectService.post(content.redirectUrl);
      });
  }

  public authorizeOrder(transactionId: string, orderId: number) {
    return this.http.post(`${this.authorizeUrl}`, { transactionId, orderId });
  }

  public checkIfAuthorized(orderId: number) {
    const time = Date.now(); // Add time to stop browser caching of response
    return this.http.get<CheckAuthorizedResponse>(`${this.checkAuthorized}?id=${orderId}&time=${time}`);
  }

  public doneSubmitting() {
    this.submitting = false;
  }

  public getWoltTrackingLink(orderId: string) {
    return this.http.get<WoltTrackingResponse>(`${this.woltTrackingUrl}/${orderId}`);
  }

  public getAgeVerificationUrl() {
    const merchantId = this.contentService.getMerchantId();
    return this.http.get<AgeVerificationUrl>(`${this.ageVerificationEndpoint}/GetUrl?MerchantId=${merchantId}`);
  }

  public validateAgeVerificationToken(token: string) {
    return this.http.post<AgeVerificationValidation>(`${this.ageVerificationEndpoint}/Validate`, { token });
  }

  set isCheckingOut(value: boolean) {
    this.isCheckingOutValue = value;
  }

  get isCheckingOut(): boolean {
    return this.isCheckingOutValue;
  }

  private handleError(error: { error: { errorMessage: string; responseCode?: number; responseCodeName?: string } }) {
    this.openSnackBar(error?.error?.errorMessage);
    this.doneSubmitting();
    return throwError(() => error);
  }

  private openSnackBar(message: string) {
    const { text, closeButton } = this.errorMessage;
    message = `${text}: ${message}`;
    this.snackBar.open(message, closeButton, {
      politeness: 'assertive',
      duration: 8000,
      panelClass: 'warn',
    });
  }
}

export const checkoutServiceFactory = (checkoutService: CheckoutService) => {
  return () => {
    return checkoutService.load();
  };
};
